import React from 'react';
import { Link } from 'react-router-dom';
import { Typography } from '@mui/material';
import {
  ChipOption,
  InfiniteScroll,
  TravelerListCard,
} from '@marageti/z4-lib';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import { UserStatus } from '@marageti/z4-sdk/lib/auth';
import AllFilters from './all-filters';
import { getChipsFromTraveler } from '../../utils/chips';
import useTravelersSearch, { tagOptions } from '../../hooks/use-travelers-search';
import useURLSearchQuery from '../../hooks/use-url-for-search-query';

const statusChips: ChipOption[] = [
  { key: 0, label: 'Enabled', value: UserStatus.ENABLED },
  { key: 1, label: 'Unverified', value: UserStatus.PENDING },
  { key: 2, label: 'Disabled', value: UserStatus.DISABLED },
];

interface DefaultSearch {
  [key: string]: string | string[];
  countryOfResidence: string[];
  fullText: string;
  status: string[];
  tags: string[];
}

const defaultSearch: DefaultSearch = {
  countryOfResidence: [],
  fullText: '',
  status: [UserStatus.ENABLED],
  tags: [],
};

const InfiniteTravelersList = () => {
  const [queryValues, updateURLQuery] = useURLSearchQuery<DefaultSearch>(defaultSearch);
  const {
    countryOfResidence,
    fullText,
    status,
    tags,
    totalZicassoTrips,
  } = queryValues;

  const {
    travelers,
    error,
    hasMore,
    isLoading,
    loadMore,
    totalHits,
  } = useTravelersSearch(queryValues);

  // Add RPT to selectedChips if totalZicassoTrips is greater than 0
  // RPT is not technically a tag
  const selectedChips = [...tags];
  if (totalZicassoTrips) {
    selectedChips.push('REPEAT_TRAVELER');
  }

  const onChangeFilter = (name: string, v: string | string[]) => {
    switch (name) {
      case 'countryOfResidence':
        updateURLQuery({ countryOfResidence: v });
        break;
      case 'fullText':
        updateURLQuery({ fullText: v });
        break;
      case 'status':
        updateURLQuery({ status: v });
        break;
      case 'tags':
        updateURLQuery({ tags: v });
        break;
      default:
        break;
    }
  };

  const formattedPartnersCount = new Intl.NumberFormat('en-US').format(totalHits);

  return (
    <>
      <AllFilters
        onChangeFilter={onChangeFilter}
        searchValue={fullText}
        selectedCountryCodes={countryOfResidence}
        tagChips={tagOptions}
        selectedTagChips={selectedChips}
        statusChips={statusChips}
        selectedStatusChips={status}
      />

      <Typography component="p" variant="h4" sx={{ mb: 1.5 }}>
        {`${formattedPartnersCount === '10,000' ? `${formattedPartnersCount}+` : formattedPartnersCount} Result${formattedPartnersCount === '1' ? '' : 's'}`}
      </Typography>

      <InfiniteScroll
        error={error}
        hasMoreItems={hasMore}
        isLoading={isLoading}
        loadMoreItems={loadMore}
      >
        {travelers?.map((traveler: Traveler) => (
          <TravelerListCard
            key={traveler.id}
            traveler={traveler}
            chips={getChipsFromTraveler(traveler)}
            component={Link}
            to={`/travelers/${traveler.id}`}
            sx={{
              '&:hover': {
                bgcolor: 'grey.A100',
              },
              textDecoration: 'none',
            }}
          />
        ))}
      </InfiniteScroll>
    </>
  );
};

export default InfiniteTravelersList;
